import React from 'react';
import { TicketPriority, TicketStatus, TICKET_PRIORITY_CONFIG, TICKET_STATUS_CONFIG } from './types';

function timeAgoStr(date: string): string {
  try {
    const diff = Date.now() - new Date(date).getTime();
    const secs = Math.floor(diff / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months}mo ago`;
    return `${Math.floor(months / 12)}y ago`;
  } catch {
    return '—';
  }
}

interface TicketBadgeProps {
  value: string;
  config: Record<string, { label: string; color: string; dotColor: string }>;
  size?: 'sm' | 'default';
}

export const TicketBadge: React.FC<TicketBadgeProps> = ({ value, config, size = 'default' }) => {
  const entry = config[value] || { label: value, color: 'bg-muted text-muted-foreground', dotColor: 'bg-muted-foreground' };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full font-medium ${
      size === 'sm' ? 'px-2 py-0.5 text-[10px]' : 'px-2.5 py-1 text-xs'
    } ${entry.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${entry.dotColor}`} />
      {entry.label}
    </span>
  );
};

export const StatusBadge: React.FC<{ status: TicketStatus; size?: 'sm' | 'default' }> = ({ status, size }) => (
  <TicketBadge value={status} config={TICKET_STATUS_CONFIG} size={size} />
);

export const PriorityBadge: React.FC<{ priority: TicketPriority; size?: 'sm' | 'default' }> = ({ priority, size }) => (
  <TicketBadge value={priority} config={TICKET_PRIORITY_CONFIG} size={size} />
);

export const TicketNumberBadge: React.FC<{ number: number }> = ({ number }) => (
  <span className="text-xs font-mono font-semibold text-muted-foreground bg-muted px-2 py-0.5 rounded">
    #{number}
  </span>
);

export const TimeAgo: React.FC<{ date: string }> = ({ date }) => (
  <span className="text-xs text-muted-foreground">{timeAgoStr(date)}</span>
);

export const UserAvatar: React.FC<{
  name?: string;
  avatar?: string | null;
  size?: 'sm' | 'default' | 'lg';
}> = ({ name, avatar, size = 'default' }) => {
  const sizeClass = size === 'sm' ? 'w-6 h-6 text-[10px]' : size === 'lg' ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs';
  const initial = (name?.[0] || '?').toUpperCase();

  return (
    <div className={`${sizeClass} rounded-full bg-workspace-accent/20 text-workspace-accent font-semibold flex items-center justify-center overflow-hidden flex-shrink-0`}>
      {avatar ? (
        <img src={avatar} alt={name} className="w-full h-full object-cover" />
      ) : (
        initial
      )}
    </div>
  );
};

export const TicketRowSkeleton: React.FC = () => (
  <tr className="border-b border-border animate-pulse">
    {[...Array(7)].map((_, i) => (
      <td key={i} className="px-4 py-3.5">
        <div className="h-4 bg-muted rounded w-3/4" />
      </td>
    ))}
  </tr>
);
